// Domain model + pure helpers for the Lead Dashboard, ported from the standalone
// inquiry-register prototype. No seed data — the dropdown vocabularies below are
// the only fixed lists. Data will come from the backend once endpoints are wired.

import { CUSTOMER_TYPES } from '@/api/price-master';

// Lead category mirrors the Party Master "Customer Type" vocabulary (single
// source), so a lead's category matches the customer type it converts into.
export const LEAD_CATEGORIES = CUSTOMER_TYPES;
export const LEAD_SOURCES = ['Instagram', 'Referral', 'Telephonic', 'Website', 'Walk-in', 'Other'] as const;
export const LEAD_STATUSES = ['Open', 'Contacted', 'Sampling/Design', 'Negotiation', 'Converted', 'Lost'] as const;
export const COMM_TYPES = ['Call', 'Instagram DM', 'WhatsApp', 'Site visit', 'Sample/design shared', 'Order confirmed', 'Other'] as const;

export type LeadCategory = (typeof LEAD_CATEGORIES)[number];
export type LeadSource = (typeof LEAD_SOURCES)[number];
export type LeadStatus = (typeof LEAD_STATUSES)[number];
export type CommType = (typeof COMM_TYPES)[number];
export type RangeKey = 'week' | 'month' | 'year' | 'all';

/** Statuses still in the active pipeline (not Converted / not Lost). */
export const PENDING_STATUSES: LeadStatus[] = ['Open', 'Contacted', 'Sampling/Design', 'Negotiation'];

/** Roles allowed to reassign a lead that already has an owner. */
export const LEAD_REASSIGN_ROLES = ['SUPER_ADMIN', 'DIRECTOR', 'SALES_MGR'];

export interface LeadComm {
  id: string;
  date: string; // yyyy-mm-dd
  type: CommType;
  by: string;
  note: string;
}

export interface Lead {
  id: string;
  date: string; // yyyy-mm-dd — when the inquiry came in
  customer: string;
  contactNumber: string; // phone or WhatsApp
  email: string;
  category: LeadCategory;
  source: LeadSource;
  referralName: string;
  assignedTo: string;
  samplingCostInr: number;
  status: LeadStatus;
  valueInr: number;
  notes: string;
  comms: LeadComm[];
  /** yyyy-mm-dd — next scheduled follow-up (optional). */
  nextFollowUp?: string;
  /** Set once the lead is converted — the Party (customer) it became. */
  convertedCode?: string;
}

export const isPending = (l: Lead): boolean => PENDING_STATUSES.includes(l.status);

export function inRange(dateStr: string, range: RangeKey): boolean {
  const d = new Date(dateStr);
  const now = new Date();
  if (range === 'week') {
    const w = new Date(now);
    w.setDate(now.getDate() - 7);
    return d >= w && d <= now;
  }
  if (range === 'month') return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  if (range === 'year') return d.getFullYear() === now.getFullYear();
  return true;
}

export const filterByRange = (leads: Lead[], range: RangeKey): Lead[] => leads.filter((l) => inRange(l.date, range));

const commDates = (l: Lead): string[] => (l.comms ?? []).map((c) => c.date).sort();
export const firstContact = (l: Lead): string => { const d = commDates(l); return d.length ? d[0] : l.date; };
export const lastUpdate = (l: Lead): string => { const d = commDates(l); return d.length ? d[d.length - 1] : l.date; };
export const commCount = (l: Lead): number => (l.comms ?? []).length;

export interface LeadStats {
  total: number;
  pendingCount: number;
  convertedCount: number;
  lostCount: number;
  conversionRate: number;
  totalValue: number;
}

export function computeStats(leads: Lead[]): LeadStats {
  const total = leads.length;
  const converted = leads.filter((l) => l.status === 'Converted');
  const lost = leads.filter((l) => l.status === 'Lost');
  const pending = leads.filter(isPending);
  return {
    total,
    pendingCount: pending.length,
    convertedCount: converted.length,
    lostCount: lost.length,
    conversionRate: total ? Math.round((converted.length / total) * 100) : 0,
    totalValue: converted.reduce((s, l) => s + l.valueInr, 0),
  };
}

export interface CategoryRow {
  category: LeadCategory;
  total: number;
  pending: number;
  converted: number;
  lost: number;
  rate: number;
}

export function categoryBreakdown(leads: Lead[]): CategoryRow[] {
  return LEAD_CATEGORIES.map((category) => {
    const rows = leads.filter((l) => l.category === category);
    const converted = rows.filter((l) => l.status === 'Converted').length;
    return {
      category,
      total: rows.length,
      pending: rows.filter(isPending).length,
      converted,
      lost: rows.filter((l) => l.status === 'Lost').length,
      rate: rows.length ? Math.round((converted / rows.length) * 100) : 0,
    };
  });
}

export interface PersonRow {
  name: string;
  inquiries: number;
  pending: number;
  converted: number;
  rate: number;
  valueClosed: number;
}

export function personStats(leads: Lead[], person: string): PersonRow {
  const rows = leads.filter((l) => l.assignedTo === person);
  const convertedRows = rows.filter((l) => l.status === 'Converted');
  return {
    name: person,
    inquiries: rows.length,
    pending: rows.filter(isPending).length,
    converted: convertedRows.length,
    rate: rows.length ? Math.round((convertedRows.length / rows.length) * 100) : 0,
    valueClosed: convertedRows.reduce((s, l) => s + l.valueInr, 0),
  };
}

export function bySource(leads: Lead[]): { name: LeadSource; value: number }[] {
  return LEAD_SOURCES.map((name) => ({ name, value: leads.filter((l) => l.source === name).length })).filter((x) => x.value > 0);
}

export function byMonth(leads: Lead[]): { month: string; inquiries: number }[] {
  const map = new Map<string, number>();
  for (const l of leads) {
    const key = l.date.slice(0, 7);
    map.set(key, (map.get(key) ?? 0) + 1);
  }
  return Array.from(map.entries())
    .map(([month, inquiries]) => ({ month, inquiries }))
    .sort((a, b) => a.month.localeCompare(b.month));
}

export interface AccountRow {
  /** Group key: customer + category (also what the search matches). */
  name: string;
  customer: string;
  category: string;
  inquiries: number;
  converted: number;
  value: number;
}

export function accountsRollup(leads: Lead[]): AccountRow[] {
  const map = new Map<string, AccountRow>();
  for (const l of leads) {
    // Only append the category when the lead actually has one — otherwise the
    // label shows a dangling "— " for uncategorised (imported) leads.
    const key = l.category ? `${l.customer} — ${l.category}` : l.customer;
    const a = map.get(key) ?? { name: key, customer: l.customer, category: l.category ?? '', inquiries: 0, converted: 0, value: 0 };
    a.inquiries += 1;
    if (l.status === 'Converted') { a.converted += 1; a.value += l.valueInr; }
    map.set(key, a);
  }
  return Array.from(map.values()).sort((a, b) => b.inquiries - a.inquiries);
}

// Prefixed with a non-digit so a new lead's client id is never all-numeric —
// saveLead() routes numeric ids to update() (existing Party) vs create().
export const uid = (): string => 'new_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
